import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, KeyRound, Copy } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { adminApi, formatApiError } from "@/lib/adminApi";

const inputCls =
  "w-full mt-1 mb-4 bg-[#141414] border border-[#222] focus:border-[#D4AF37] outline-none text-sm px-3 py-2 text-white";

const ChangePasswordForm = () => {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await adminApi.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success("Senha alterada com sucesso");
      setCur("");
      setNext("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="change-password-form">
      <label className="text-xs text-white/50">Senha atual</label>
      <input data-testid="change-password-current" type="password" required value={cur} onChange={(e) => setCur(e.target.value)} className={inputCls} />
      <label className="text-xs text-white/50">Nova senha (mínimo 10 caracteres)</label>
      <input data-testid="change-password-new" type="password" required minLength={10} value={next} onChange={(e) => setNext(e.target.value)} className={inputCls} />
      <button data-testid="change-password-submit" disabled={busy} className="btn-secondary-outline text-xs disabled:opacity-40">
        <KeyRound size={14} /> {busy ? "Salvando..." : "Trocar senha"}
      </button>
    </form>
  );
};

const RecoveryCodes = () => {
  const [remaining, setRemaining] = useState(null);
  const [codes, setCodes] = useState([]);

  useEffect(() => {
    adminApi.get("/auth/me").then((r) => setRemaining(r.data.recovery_codes_remaining));
  }, []);

  const generate = async () => {
    if (remaining > 0 && !window.confirm("Gerar novos códigos invalida os códigos anteriores. Continuar?")) return;
    const r = await adminApi.post("/auth/recovery-codes");
    setCodes(r.data.codes);
    setRemaining(r.data.codes.length);
  };

  return (
    <div data-testid="recovery-codes-section">
      <p className="text-xs text-white/60 mb-3">
        Códigos de uso único para redefinir a senha caso você a esqueça. Guarde-os em local seguro (fora do celular, se possível).
        Disponíveis: <span data-testid="recovery-codes-remaining" className="text-[#D4AF37]">{remaining ?? "—"}</span>
      </p>
      {codes.length > 0 && (
        <div className="mb-3">
          <div data-testid="recovery-codes-list" className="grid grid-cols-2 gap-1.5 font-mono text-sm bg-[#050505] border border-[#D4AF37]/40 p-3">
            {codes.map((c) => <span key={c}>{c}</span>)}
          </div>
          <p className="text-[11px] text-amber-400 mt-2">Estes códigos não serão exibidos novamente.</p>
          <button data-testid="recovery-codes-copy" onClick={() => navigator.clipboard.writeText(codes.join("\n")).then(() => toast.success("Copiado"))} className="text-xs text-[#D4AF37] hover:underline mt-1 inline-flex items-center gap-1">
            <Copy size={12} /> Copiar códigos
          </button>
        </div>
      )}
      <button data-testid="recovery-codes-generate" onClick={generate} className="btn-secondary-outline text-xs">
        Gerar códigos de recuperação
      </button>
    </div>
  );
};

export const AdminSecurity = () => (
  <Dialog>
    <DialogTrigger asChild>
      <button data-testid="admin-security-btn" className="btn-secondary-outline text-xs">
        <ShieldCheck size={14} /> Segurança
      </button>
    </DialogTrigger>
    <DialogContent data-testid="admin-security-dialog" className="bg-[#0A0A0A] border-[#222] text-white rounded-none max-w-md">
      <DialogHeader>
        <DialogTitle className="font-heading text-2xl font-light">Segurança da conta</DialogTitle>
      </DialogHeader>
      <ChangePasswordForm />
      <div className="border-t border-[#222] my-4" />
      <RecoveryCodes />
    </DialogContent>
  </Dialog>
);
